import type {
  ApiKeyAdminEventTypeCatalogQuery,
  OutboundWebhookEventType,
  WebhookPayloadShape,
} from "../../generated/graphql";

export type EventTypeCatalog =
  ApiKeyAdminEventTypeCatalogQuery["apiKeyAdminEventTypeCatalog"];

export type EventTypeOption = {
  eventType: OutboundWebhookEventType;
  deprecated: boolean;
};

export type EventTypeOptions = {
  /** Types the key can receive with these settings, in catalog order. */
  options: EventTypeOption[];
  /**
   * Selected types outside `options`. The backend rejects a save that lists
   * any of them, and the dialogs block save while this is non-empty. The
   * picker flags these for the admin to remove, since dropping them for the
   * admin could empty the list, and an empty list means every event type.
   */
  outOfScope: OutboundWebhookEventType[];
};

/**
 * Works out which event types the picker offers for a key: the types its
 * eligible shapes carry (the standard shapes when `payloadShapes` is empty),
 * minus `TRANSFER_COMPLETED` / `RECOVERY_COMPLETED` unless
 * `emitTerminalHandoffEvents` is on. Mirrors `findUndeliverableEventTypes` in
 * the backend, which runs the same check on save.
 */
export const getEventTypeOptions = ({
  catalog,
  payloadShapes,
  emitTerminalHandoffEvents,
  eventTypes,
}: {
  catalog: EventTypeCatalog;
  payloadShapes: readonly WebhookPayloadShape[];
  emitTerminalHandoffEvents: boolean;
  eventTypes: readonly OutboundWebhookEventType[];
}): EventTypeOptions => {
  const listedShapes = new Set(payloadShapes);
  const offered = new Map<OutboundWebhookEventType, EventTypeOption>();
  for (const shapeEntry of catalog) {
    const eligible =
      listedShapes.size === 0
        ? shapeEntry.isStandard
        : listedShapes.has(shapeEntry.shape);
    if (!eligible) {
      continue;
    }
    for (const entry of shapeEntry.eventTypes) {
      if (entry.requiresTerminalHandoffOptIn && !emitTerminalHandoffEvents) {
        continue;
      }
      offered.set(entry.eventType, {
        eventType: entry.eventType,
        deprecated: entry.deprecated,
      });
    }
  }
  return {
    options: [...offered.values()],
    outOfScope: eventTypes.filter((eventType) => !offered.has(eventType)),
  };
};
